import styled, { css } from 'styled-components';
import { headerBackground } from '../../styles';

const EASE_OUT = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

// Animated underline shared by the nav links and Logout: a bar that grows out from the
// centre on hover/focus (at half strength) and settles at full width for the current section.
export const navUnderline = css`
  position: relative;
  text-decoration: none;
  opacity: 0.85;
  transition: opacity 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    left: var(--underline-inset, 0);
    right: var(--underline-inset, 0);
    bottom: 0;
    height: 3px;
    border-radius: 2px;
    background-color: currentColor;
    opacity: 0.5;
    transform: scaleX(0);
    transform-origin: center;
    transition: transform 0.25s ${EASE_OUT}, opacity 0.25s ease;
  }

  /* Hover and focus-visible are separate rules: a browser without :focus-visible would
     otherwise drop the whole selector list, hover included */
  &:hover {
    opacity: 1;
    text-decoration: none;
  }

  &:focus-visible {
    opacity: 1;
    text-decoration: none;
  }

  &:hover::after {
    transform: scaleX(1);
  }

  &:focus-visible::after {
    transform: scaleX(1);
  }

  &:active {
    opacity: 0.7;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &::after {
      transition: none;
    }
  }
`;

// List containing Navigation Links excluding Logout
export const LinksGroup = styled.ul`
  display: flex;
  flex-direction: column;

  li > a {
    ${navUnderline}
    display: inline-block;
    padding: 0 0 6px;
  }

  /* Current section: full-strength underline and full opacity */
  li > a[aria-current='page'] {
    font-weight: 700;
    opacity: 1;

    &::after {
      opacity: 1;
      transform: scaleX(1);
    }
  }

  ${({ theme }) => theme.media.desktopUp`
    flex-direction: row;

    li > a {
      padding: 15px 15px 12px;
      /* Underline spans the text, not the link's side padding */
      --underline-inset: 15px;
    }
  `}
`;

// List containing Navigation Links including Logout
export const LinksContainer = styled.ul`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: space-between;
  text-align: right;

  li {
    margin-bottom: 15px;
  }

  ${({ theme }) => theme.media.desktopUp`
    flex-direction: row;
    align-items: center;
    padding: 0 30px;

    li {
      margin-bottom: 0;
    }

    li > button {
      margin-left: 30px;
    }

  `}
`;

export const NavMenu = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: space-between;
  padding: 20px 15px 20px 60px;
  ${headerBackground}
  height: 100vh;
  transition: transform 0.3s ease-in-out;
  width: 100%;

  & > * {
    margin-bottom: 10px;
    margin-left: auto;
  }

  & > *:last-child {
    margin-top: 10px;
    margin-bottom: 5px;
  }

  ${({ theme }) => theme.media.desktopUp`
    flex-direction: row;
    align-items: center;
    margin-top: 0;
    padding: 0;
    background: transparent;
    height: auto;
    text-align: none;
    width: auto;
    transform: none;

    & > * {
      margin-bottom: 0;
      margin-left: 0;
    }

    & > *:last-child {
      margin-top: 0;
      margin-bottom: 5px;
    }
  `}
`;

// Opening delays: nav links one after another, then Logout
const linkDelays = [1, 2, 3, 4, 5]
  .map(n => `${LinksGroup} > li:nth-child(${n}) { transition-delay: ${60 + n * 40}ms; }`)
  .join('');

const menuItemStagger = css`
  ${linkDelays}

  ${LinksContainer} > li {
    transition-delay: 300ms;
  }
`;

// Contains the List of Navigation Links
export const HeaderNav = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  top: 100%;
  right: 0;
  z-index: 2;
  height: auto;
  /* Slide in from the right edge; hidden (and out of the tab order) once closed */
  transform: ${({ open }) => (open ? 'translateX(0)' : 'translateX(100%)')};
  visibility: ${({ open }) => (open ? 'visible' : 'hidden')};
  /* Animate only after the menu button is used; resizing across the desktop breakpoint
     must switch layouts instantly instead of sliding the desktop nav off to the right */
  transition: ${({ open, $animate }) => {
    if (!$animate) return 'none';
    return open
      ? 'transform 0.3s ease-in-out'
      : 'transform 0.3s ease-in-out, visibility 0s linear 0.3s';
  }};

  /* Mobile: menu items drift in one after another as the menu opens */
  ${LinksContainer} li {
    opacity: ${({ open }) => (open ? 1 : 0)};
    transform: ${({ open }) => (open ? 'none' : 'translateX(16px)')};
    transition: ${({ $animate }) =>
    ($animate ? `opacity 0.25s ease, transform 0.3s ${EASE_OUT}` : 'none')};
  }

  ${({ open }) => open && menuItemStagger}

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    ${LinksContainer} li {
      transition: none;
    }
  }

  ${({ theme }) => theme.media.desktopUp`
    ${LinksContainer} li {
      opacity: 1;
      transform: none;
      transition: none;
    }

    position: static;
    flex-direction: row;
    transform: none;
    visibility: visible;
    transition: none;
    width: 90%;
    justify-content: flex-end;
    top: 0;
    right: 0;
    z-index: 1;
  `}
`;
